import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { failStatus, GenericRequest, successStatus } from '@core/models/request-status.model';
import { environment } from '@environments/environment';
import { Cart, CartItem, GetCartResponse } from '@b2b/features/cart/utilities/cart.model';
import { normalizeCart } from '@b2b/features/cart/utilities/cart.normalizer';
import { LocalStorageService } from '@shared/tools/local-storage.service';
import { CartListResponse } from '@b2b/features/cart/utilities/cart-list-model';
import { CART_ID_KEY } from '@shared/tools/constants';

@Injectable({
  providedIn: 'root',
})
export class CartConnector {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private localStorage: LocalStorageService,
  ) {}

  getMiniCart(cartID: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/${cartID}/mini`;

    return this.http.get<GetCartResponse>(url).pipe(
      switchMap(cart => {
        return this.getProductsInCart(cart);
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  get(cartID: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/${cartID}`;

    return this.http.get<GetCartResponse>(url).pipe(
      switchMap(cart => {
        return this.getProductsInCart(cart);
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getLatest(customerId?: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/user/${customerId}/latest`;

    return this.http.get<GetCartResponse>(url).pipe(
      switchMap(cart => {
        if (!cart) {
          return of({
            ...failStatus,
            data: null,
            message: 'No cart found',
          });
        }

        return this.getProductsInCart(cart);
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getOrCreate(): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/get-or-create`;
    return this.http.post<GetCartResponse>(url, {}).pipe(
      switchMap(cart => {
        return this.getProductsInCart(cart);
      }),
      catchError(error => {
        return of({
          ...failStatus,
          message: error?.message || '',
        });
      }),
    );
  }

  addCartItem(updatedItem: CartItem): Observable<GenericRequest<Cart>> {
    const cartCode = this.localStorage.getItem(CART_ID_KEY) || undefined;

    const url = `${environment.apiService}/cart/add`;

    const body = {
      ...updatedItem,
      cartCode,
    };

    return this.http.post<GetCartResponse>(url, body).pipe(
      switchMap(cart => {
        return this.getProductsInCart(cart);
      }),
      catchError(response => {
        return of({ ...failStatus, message: response.error.message });
      }),
    );
  }

  updateCart(name: string): Observable<GenericRequest<Cart>> {
    const cartCode = this.localStorage.getItem(CART_ID_KEY) || '';
    const url = `${environment.apiService}/cart/${cartCode}`;

    return this.http
      .patch<GetCartResponse>(url, {
        name,
      })
      .pipe(
        switchMap(cart => {
          return this.getProductsInCart(cart);
        }),
        catchError(() => {
          return this.translateService.get('generic.requestError').pipe(
            map(message => ({
              ...failStatus,
              message,
            })),
          );
        }),
      );
  }

  updateCartItem(entryNumber: number, updatedItem: CartItem): Observable<GenericRequest<Cart>> {
    const cartCode = this.localStorage.getItem(CART_ID_KEY) || '';

    const url = `${environment.apiService}/cart/${cartCode}/entries/${entryNumber}`;

    return this.http.patch<GetCartResponse>(url, updatedItem).pipe(
      switchMap(cart => {
        return this.getProductsInCart(cart);
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  deleteCartItem(entryNumber: number, cartCode: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/${cartCode}/entries/${entryNumber}`;

    return this.http.delete(url).pipe(
      map(() => {
        return { ...successStatus };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  deleteCart(cartCode: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/${cartCode}`;

    return this.http.delete(url).pipe(
      map(() => {
        return { ...successStatus };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getCartLists(page: number, customerId: string, b2bUnitId: string): Observable<GenericRequest<CartListResponse>> {
    const url = environment.apiService + `/cart/user/${customerId}/${b2bUnitId}?page=${page}&pageSize=10`;

    return this.http.get<CartListResponse>(url).pipe(
      map(data => {
        return { ...successStatus, data: data };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  applyCoupon(cartCode: string, couponCode: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/${cartCode}/coupon`;

    return this.http.post<GetCartResponse>(url, { couponCode }).pipe(
      switchMap(cart => this.getProductsInCart(cart)),
      catchError(response => {
        return of({ ...failStatus, message: response.error?.message || '' });
      }),
    );
  }

  removeCoupon(cartCode: string): Observable<GenericRequest<Cart>> {
    const url = `${environment.apiService}/cart/${cartCode}/coupon`;

    return this.http.delete<GetCartResponse>(url).pipe(
      switchMap(cart => this.getProductsInCart(cart)),
      catchError(response => {
        return of({ ...failStatus, message: response.error?.message || '' });
      }),
    );
  }

  saveCart(name: string): Observable<GenericRequest<any>> {
    const url = `${environment.apiService}/cart`;
    const body = {
      name,
    };

    return this.http.post<GetCartResponse>(url, body).pipe(
      map(data => {
        this.localStorage.setItem(CART_ID_KEY, data.code);
        return { ...successStatus, data: data };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getAvailableCredit(): Observable<string | null> {
    return this.http
      .get<{ limiteCreditoDisponivel: string | null }>(`${environment.apiService}/customer/credit/available`)
      .pipe(
        map(res => res.limiteCreditoDisponivel),
        catchError(() => of(null)),
      );
  }

  getProductsInCart(cart: GetCartResponse): Observable<GenericRequest<Cart>> {
    this.localStorage.setItem(CART_ID_KEY, cart.code);

    return of({
      ...successStatus,
      data: normalizeCart(cart),
    });
  }
}
